import { useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect, useRef, useState } from 'react'

import { getRealtimeRef } from '@/integrations/firebase'

import { REGEN_INVALIDATION_KEYS } from './regen-invalidation'

export type RegenRealtime = {
  status?: 'idle' | 'running' | 'done' | 'error'
  stale?: boolean
}

/**
 * Live regen state (status + stale) from RTDB, mirrored by the backend on every
 * regen transition. Returns undefined until the first snapshot — callers should
 * fall back to the value on the individual response.
 *
 * When a run completes (running → done/error), invalidates the queries whose
 * data the regen rewrites so the freshly generated description / Hitta stöd
 * appear without a manual refresh.
 */
export const useRegenRealtime = (individualId?: string): RegenRealtime | undefined => {
  const [value, setValue] = useState<RegenRealtime>()
  const queryClient = useQueryClient()
  const previousStatus = useRef<RegenRealtime['status']>(undefined)

  useEffect(() => {
    setValue(undefined)
    previousStatus.current = undefined
    if (!individualId) return

    const ref = getRealtimeRef(`individuals/${individualId}/regen`)
    return onValue(ref, snapshot => {
      const next = snapshot.exists() ? (snapshot.val() as RegenRealtime) : undefined
      setValue(next)

      const justFinished =
        previousStatus.current === 'running' &&
        (next?.status === 'done' || next?.status === 'error')

      if (justFinished) {
        for (const key of REGEN_INVALIDATION_KEYS) {
          queryClient.invalidateQueries({ queryKey: [key] })
        }
      }

      previousStatus.current = next?.status
    })
  }, [individualId, queryClient])

  return value
}
